import { Phone, Mail, Clock, MapPin } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import Reveal from "@/components/Reveal";
import EnquiryForm from "@/components/EnquiryForm";
import Seo from "@/components/Seo";
import { DEPARTMENTS, COMPANY } from "@/lib/data";

const HERO = "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?auto=format&fit=crop&w=1800&q=80";

export default function ContactPage() {
  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact Simba Travel — UK GSA for Air Zimbabwe | Phone & Email"
        description="Contact Simba Travel's London office: agency sales, ticketing, refunds and groups desks. Phone +44 20 3023 7701, email, opening hours 09:00–23:00 UK time, seven days a week."
      />
      <section className="relative h-[55vh] min-h-[400px] -mt-24 flex items-end overflow-hidden">
        <img src={HERO} alt="London" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-midnight to-midnight/30" />
        <div className="relative container-edge pb-14 text-linen">
          <div className="overline text-ochre mb-4">Contact</div>
          <h1 className="editorial-h1 max-w-3xl">Let's talk.</h1>
        </div>
      </section>

      <section className="section-pad bg-linen">
        <div className="container-edge grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-4 space-y-10">
            <div>
              <div className="overline text-terracotta">Company</div>
              <h3 className="font-serif text-2xl mt-2">{COMPANY.legalName}</h3>
              <div className="text-sm text-muted2 mt-2">Trading as {COMPANY.name}</div>
              <div className="text-xs text-muted2 mt-1">Registration No. {COMPANY.registration}</div>
            </div>

            <div className="flex items-start gap-4">
              <MapPin className="w-5 h-5 stroke-1 text-terracotta mt-1" />
              <div>
                <div className="overline">Head office</div>
                <address className="not-italic text-sm mt-2 leading-relaxed" data-testid="contact-address">
                  13 Hogarth Place, London<br />
                  SW5 0QT, United Kingdom
                </address>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <Clock className="w-5 h-5 stroke-1 text-terracotta mt-1" />
              <div>
                <div className="overline">Operating hours</div>
                <div className="text-sm mt-2">{COMPANY.hours}</div>
              </div>
            </div>

            <div>
              <div className="overline mb-4">Departments</div>
              <div className="space-y-4">
                {DEPARTMENTS.map((d) => (
                  <div key={d.id} className="border-b border-ink/10 pb-3" data-testid={`contact-dept-${d.id}`}>
                    <div className="text-sm font-medium">{d.name}</div>
                    <div className="text-xs flex items-center gap-3 mt-1 text-muted2">
                      <a href={`tel:${d.phone}`} className="hover:text-terracotta flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {d.phoneIntl}
                      </a>
                      <a href={`mailto:${d.email}`} className="hover:text-terracotta flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {d.email}
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          <div className="md:col-span-8">
            <EnquiryForm
              defaultType="general"
              title="Send us a message"
              subtitle="Use this form for any enquiry — airline partnerships, travel agent registration, corporate travel programmes or a destination question."
              testIdPrefix="contact-enquiry"
            />
          </div>
        </div>
      </section>

      {/* How we handle enquiries — SEO + trust */}
      <section className="section-pad bg-sand border-t border-ink/10" data-testid="contact-handling">
        <div className="container-edge grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="overline text-terracotta mb-4">How we respond</div>
            <h2 className="editorial-h2">A direct line to a real UK-based team.</h2>
          </div>
          <div className="md:col-span-7 md:pt-2 space-y-6 text-base leading-relaxed text-ink/80 font-light">
            <p>
              Every enquiry submitted through this website reaches our London contact centre. Fare quotes and travel-agent trade enquiries are typically responded to within one UK business day; ticketing requests during our operating window (09:00–23:00, seven days a week) are handled the same day. Refund applications are acknowledged within 48 hours and processed through BSP-UK in line with the operating airline's conditions of carriage.
            </p>
            <p>
              For time-sensitive matters — including irregular operations, missed connections and disruption recovery — please always call the relevant desk directly on the numbers listed opposite. Group enquiries for ten or more passengers should be routed to the Groups desk with your proposed travel dates, ticketed cabin, party size and any special service requirements (mobility, unaccompanied minors, sports equipment, wedding parties).
            </p>
            <p>
              Airlines exploring UK representation, tour operators building an Africa programme, and corporates seeking a preferred-airline agreement can request an introductory call via the enquiry form — a member of our commercial team will follow up with a tailored proposal covering distribution, ticketing, service levels and reporting.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
